import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Crumb = {
  label: ReactNode;
  /** Parent target for a linked crumb; the last crumb is the current page. */
  href?: string;
  /** Click interceptor (e.g. filter-restore / unsaved-changes guard). */
  onClick?: () => void;
};

// Split the value + setter into two contexts so the setter identity is stable
// (the useState setter never changes) — pages can publish crumbs from an effect
// without re-triggering on every provider render.
const CrumbsContext = createContext<Crumb[]>([]);
const SetCrumbsContext = createContext<(crumbs: Crumb[]) => void>(() => {});
const HeaderActionsContext = createContext<ReactNode>(null);
const SetHeaderActionsContext = createContext<(node: ReactNode) => void>(
  () => {},
);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [headerActions, setHeaderActions] = useState<ReactNode>(null);
  return (
    <SetCrumbsContext.Provider value={setCrumbs}>
      <SetHeaderActionsContext.Provider value={setHeaderActions}>
        <CrumbsContext.Provider value={crumbs}>
          <HeaderActionsContext.Provider value={headerActions}>
            {children}
          </HeaderActionsContext.Provider>
        </CrumbsContext.Provider>
      </SetHeaderActionsContext.Provider>
    </SetCrumbsContext.Provider>
  );
}

export function useBreadcrumbs(): Crumb[] {
  return useContext(CrumbsContext);
}

export function useHeaderActions(): ReactNode {
  return useContext(HeaderActionsContext);
}

/**
 * Publish this page's breadcrumb trail to the app header. `key` is a stable
 * string that changes only when the visible crumb content changes — the effect
 * re-runs on key change and clears the trail on unmount.
 */
export function useSetBreadcrumbs(crumbs: Crumb[], key: string) {
  const setCrumbs = useContext(SetCrumbsContext);
  useEffect(() => {
    setCrumbs(crumbs);
    return () => setCrumbs([]);
    // `key` encodes the crumbs' content; `setCrumbs` is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCrumbs, key]);
}

/**
 * Publish right-aligned actions into the app header (e.g. Viewing as), so they
 * sit on the same row as the breadcrumb trail.
 */
export function useSetHeaderActions(node: ReactNode, key: string) {
  const setHeaderActions = useContext(SetHeaderActionsContext);
  useEffect(() => {
    setHeaderActions(node);
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeaderActions, key]);
}
